import React from 'react';
import {
  Box, Typography, Grid, Paper, LinearProgress, Chip, Stack,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import BlenderIcon from '@mui/icons-material/ViewInAr';
import DevicesIcon from '@mui/icons-material/Devices';
import CellTowerIcon from '@mui/icons-material/CellTower';

const stats = [
  { label: 'Years Streaming', value: '1+' },
  { label: 'Models Created', value: '30+' },
  { label: 'PCBs Designed', value: '2+' },
  { label: 'Boxes pwned', value: '100+' },
];

const skills = [
  { label: 'VTubing & Live Performance', value: 90 },
  { label: '3D Modelling (Blender / ZBrush)', value: 88 },
  { label: 'Merch & Art', value: 88 },
  { label: 'PCB Design (KiCad)', value: 75 },
  { label: 'Hacking & CTF', value: 82 },
  { label: 'Embedded / Firmware Dev', value: 70 },
  { label: 'Web / UI Dev', value: 78 },
];

const traits = [
  { icon: <BlenderIcon />, label: '3D Artist', desc: 'VRM avatars, props & environments' },
  { icon: <CellTowerIcon />, label: 'VTuber', desc: 'Live2D & 3D streaming' },
  { icon: <TerminalIcon />, label: 'Hacker', desc: 'CTF, RE, hardware hacking' },
  { icon: <DevicesIcon />, label: 'PCB Maker', desc: 'Custom electronics & IoT' },
];

export default function About() {
  return (
    <Box
      id="about"
      sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 8 },
        maxWidth: 1200,
        mx: 'auto',
      }}
    >
      <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem' }}>
        01 — ABOUT
      </Typography>
      <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 6, mt: 1 }}>
        Who Am I?
      </Typography>

      <Grid container spacing={6} alignItems="flex-start">
        {/* Left column */}
        <Grid size={{ xs: 12, md: 5 }}>
          {/* Avatar placeholder */}
          <Box
            sx={{
              width: '100%',
              aspectRatio: '3/4',
              maxWidth: 380,
              mx: 'auto',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255,110,180,0.15), rgba(180,79,255,0.2))',
              border: '1px solid rgba(255,110,180,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(rgba(255,110,180,0.08) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }} />
            <Box
              component="img"
              src="/images/panekopanko.webp"
              alt="panekopanko"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
                zIndex: 1,
              }}
            />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 3, gap: 1, justifyContent: 'center' }}>
            {['she/her', 'JP/EN', 'Night Owl', 'Soldering Addict'].map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  background: 'rgba(180,79,255,0.1)',
                  border: '1px solid rgba(180,79,255,0.3)',
                  color: 'secondary.light',
                }}
              />
            ))}
          </Stack>
        </Grid>

        {/* Right column */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 2, mb: 4, fontSize: '1.05rem' }}>
            Hey! I'm <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>pan</Box>,
            a stockholm based hacker, vtuber & operator  who likes to make &amp; break stuff!
            As a fully diy vtuber, I work hard to run everything myself, keep it opensource and spread knowledge
            and (maybe a few other things) around the internet! Think hacking sessions, modelling sessions, Webdev, CTF-ery and more!
          </Typography>

          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 2, mb: 6, fontSize: '1.05rem' }}>
            My work spans from crafting VRM avatars used by other VTubers, to designing open-source
            hardware for the maker community. I believe the future is cute & totally hackable.
          </Typography>

          {/* Skills */}
          <Typography variant="h6" sx={{ mb: 3, color: 'text.primary' }}>Skills</Typography>
          <Stack spacing={2.5}>
            {skills.map((skill) => (
              <Box key={skill.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{skill.label}</Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>{skill.value}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={skill.value}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    background: 'rgba(255,110,180,0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #ff6eb4, #b44fff)',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      {/* Stats row */}
      <Grid container spacing={3} sx={{ mt: 8 }}>
        {stats.map((stat) => (
          <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
            <Paper
              sx={{
                p: 3, textAlign: 'center',
                background: 'rgba(255,110,180,0.05)',
                border: '1px solid rgba(255,110,180,0.15)',
                transition: 'all 0.3s',
                '&:hover': {
                  border: '1px solid rgba(255,110,180,0.4)',
                  background: 'rgba(255,110,180,0.08)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Typography variant="h3" sx={{
                background: 'linear-gradient(135deg, #ff6eb4, #b44fff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
                fontSize: '2.5rem',
              }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Trait cards */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        {traits.map((trait) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={trait.label}>
            <Paper
              sx={{
                p: 3,
                background: 'rgba(22,15,32,0.8)',
                border: '1px solid rgba(255,110,180,0.12)',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 0 20px rgba(255,110,180,0.15)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box sx={{ color: 'primary.main', mb: 1.5, '& svg': { fontSize: 32 } }}>
                {trait.icon}
              </Box>
              <Typography variant="h6" sx={{ mb: 0.5, fontSize: '1rem' }}>{trait.label}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{trait.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
